import { useEffect, useState } from "react";

const API_URL = "https://reportapi.beesql.com/api/admin/retailr/report";
const POST_MESSAGE_TARGET = "*";
const OAUTH_MESSAGE_TYPE = "Cloudbeds";
const PROVIDER = "Cloudbeds";
const XOPTION = "SP_beeSQLCloudbedsAuth";

const styles = {
  page: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    padding: "24px",
    background: "#f7f7f8",
    color: "#111827",
    fontFamily:
      'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  card: {
    width: "min(520px, 90vw)",
    padding: "20px",
    border: "1px solid rgba(17, 24, 39, 0.1)",
    borderRadius: "12px",
    background: "#ffffff",
    boxShadow: "0 6px 24px rgba(15, 23, 42, 0.08)",
  },
  ok: {
    color: "#15803d",
  },
  err: {
    color: "#b91c1c",
  },
  details: {
    opacity: 0.8,
    fontSize: "0.95rem",
    lineHeight: 1.5,
  },
  code: {
    padding: "2px 6px",
    borderRadius: "6px",
    background: "rgba(15, 23, 42, 0.06)",
  },
  buttonRow: {
    marginTop: "16px",
  },
  button: {
    padding: "8px 12px",
    border: "1px solid rgba(17, 24, 39, 0.2)",
    borderRadius: "8px",
    background: "transparent",
    cursor: "pointer",
  },
};

const parseQuery = (search) => {
  const params = new URLSearchParams(search);
  return {
    code: params.get("code"),
    state: params.get("state"),
    error: params.get("error"),
    error_description: params.get("error_description"),
  };
};

const getUserId = (state) => {
  if (state && state.startsWith("uid:")) {
    return state.replace("uid:", "");
  }

  return null;
};

const getTokenFromState = (state) => {
  if (state && state.startsWith("x_token:")) {
    return state.replace("x_token:", "");
  }

  return "";
};

const postToOpener = (payload) => {
  try {
    if (window.opener) {
      window.opener.postMessage(payload, POST_MESSAGE_TARGET);
    }
  } catch (_) {}
};

const CloudbedsCallback = ({ location }) => {
  const [viewState, setViewState] = useState({
    title: "Processing Cloudbeds OAuth...",
    tone: "",
    details: "Exchanging authorization code, please wait...",
  });

  useEffect(() => {
    let isMounted = true;
    let closeTimer = null;

    const run = async () => {
      const params = parseQuery(location.search);
      const userId = getUserId(params.state);
      const tokenFromState = getTokenFromState(params.state);

      if (params.error || params.error_description) {
        const payload = {
          type: OAUTH_MESSAGE_TYPE,
          status: "error",
          provider: PROVIDER,
          code: params.code,
          state: params.state,
          error: params.error || "unknown_error",
          error_description: params.error_description,
          userId,
        };

        postToOpener(payload);

        if (isMounted) {
          setViewState({
            title: "Cloudbeds OAuth failed",
            tone: "err",
            details: (
              <>
                <div>
                  <strong>Error:</strong> <code style={styles.code}>{payload.error}</code>
                </div>
                {payload.error_description ? (
                  <div style={styles.details}>{payload.error_description}</div>
                ) : null}
              </>
            ),
          });
        }

        return;
      }

      if (!params.code) {
        postToOpener({
          type: OAUTH_MESSAGE_TYPE,
          status: "error",
          provider: PROVIDER,
          code: null,
          state: params.state,
          error: "missing_params",
          userId,
        });

        if (isMounted) {
          setViewState({
            title: "Cloudbeds OAuth invalid response",
            tone: "err",
            details: (
              <div>
                Missing <code style={styles.code}>code</code> in callback.
              </div>
            ),
          });
        }

        return;
      }

      if (isMounted) {
        setViewState({
          title: "Finalizing...",
          tone: "",
          details: "Talking to server (token exchange)...",
        });
      }

      let responseBody = {};
      let httpStatus = 0;
      let httpOk = false;
      let errMsg = null;
      let success = false;

      try {
        const response = await fetch(API_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            xoption: XOPTION,
          },
          body: JSON.stringify({
            code: params.code,
            state: params.state,
            userId,
            xoption: XOPTION,
            x_token: localStorage.getItem("token") || tokenFromState,
            username: localStorage.getItem("username") || "",
            callback_url: window.location.href,
            origin: window.location.origin,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
        });

        httpStatus = response.status;
        httpOk = response.ok;
        responseBody = await response.json().catch(() => ({}));
        success = String(responseBody.status || "").toUpperCase() === "OK";
      } catch (error) {
        errMsg = error && error.message ? error.message : String(error);
      }

      if (success) {
        postToOpener({
          type: OAUTH_MESSAGE_TYPE,
          status: "success",
          provider: PROVIDER,
          code: params.code,
          state: params.state,
          userId,
          server: { status: httpStatus, ok: httpOk, body: responseBody },
        });

        if (isMounted) {
          setViewState({
            title: "Cloudbeds authenticated",
            tone: "ok",
            details: (
              <>
                <div>
                  <strong>Status</strong>: <code style={styles.code}>OK</code>
                </div>
                <div style={styles.details}>
                  Your Cloudbeds connection is now active. You can close this window.
                </div>
              </>
            ),
          });
        }

        closeTimer = window.setTimeout(() => {
          try {
            window.close();
          } catch (_) {}
        }, 2000);

        return;
      }

      const serverError =
        responseBody.error_message ||
        responseBody.errormessage ||
        responseBody.error ||
        responseBody.message ||
        errMsg ||
        "Token exchange failed";

      postToOpener({
        type: OAUTH_MESSAGE_TYPE,
        status: "error",
        provider: PROVIDER,
        code: params.code,
        state: params.state,
        userId,
        server: { status: httpStatus, ok: httpOk, body: responseBody },
        error: "exchange_failed",
        error_description: serverError,
      });

      if (isMounted) {
        setViewState({
          title: "Cloudbeds OAuth failed",
          tone: "err",
          details: (
            <>
              <div>
                <strong>Server error:</strong> <code style={styles.code}>{serverError}</code>
              </div>
              <div style={styles.details}>
                HTTP {httpStatus || "?"}
                {httpOk ? "" : " (not OK)"}
              </div>
            </>
          ),
        });
      }
    };

    run();

    return () => {
      isMounted = false;
      if (closeTimer) {
        window.clearTimeout(closeTimer);
      }
    };
  }, [location.search]);

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h2 style={viewState.tone ? styles[viewState.tone] : undefined}>{viewState.title}</h2>
        <div style={styles.details}>{viewState.details}</div>
        <div style={styles.buttonRow}>
          <button onClick={() => window.close()} style={styles.button} type="button">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CloudbedsCallback;
